import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Upload, FileUp, Shield, ShieldCheck, ShieldAlert, Loader2, X, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Scanner() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    // Check file size (max 32MB)
    if (selectedFile.size > 32 * 1024 * 1024) {
      toast.error('File size exceeds 32MB limit');
      return;
    }
    setFile(selectedFile);
    setResult(null);
  };

  const removeFile = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleScan = async () => {
    if (!file) return;

    setScanning(true);
    setProgress(0);
    setResult(null);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 500);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/scan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      clearInterval(progressInterval);
      setProgress(100);
      setResult(response.data);
      
      if (response.data.is_safe) {
        toast.success('File is safe! No threats detected.');
      } else if (response.data.is_safe === false) {
        toast.error('Warning: Potential threats detected!');
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Scan failed:', error);
      toast.error(error.response?.data?.detail || 'Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const getStatusColor = () => {
    if (!result) return 'blue';
    if (result.is_safe === true) return 'green';
    if (result.is_safe === false) return 'red';
    return 'yellow';
  };

  const StatusIcon = () => {
    if (!result) return <Shield className="w-8 h-8 text-blue-400" />;
    if (result.is_safe === true) return <ShieldCheck className="w-8 h-8 text-green-400" />;
    if (result.is_safe === false) return <ShieldAlert className="w-8 h-8 text-red-400" />;
    return <AlertTriangle className="w-8 h-8 text-yellow-400" />;
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-950 to-gray-950 -z-10" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Shield className="w-3 h-3 mr-1" />
            Powered by VirusTotal
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Outfit']">
            Virus <span className="text-gradient">Scanner</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Upload any file to scan for malware and security threats. Free, fast, and secure.
          </p>
        </div>

        {/* Upload Area */}
        <Card className="bg-gray-900/50 border-white/10 mb-8">
          <CardContent className="p-8">
            <div
              className={`drop-zone rounded-xl p-12 text-center cursor-pointer transition-all ${
                dragOver ? 'dragover border-blue-500 bg-blue-500/10' : ''
              } ${file ? 'border-solid border-green-500/50' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              data-testid="scanner-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="scanner-file-input"
              />
              
              {file ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                    <FileText className="w-8 h-8 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-gray-500 text-sm">{formatFileSize(file.size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto">
                    <Upload className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Drop your file here</p>
                    <p className="text-gray-500 text-sm">or click to browse</p>
                  </div>
                  <p className="text-gray-600 text-xs">Maximum file size: 32MB</p>
                </div>
              )}
            </div>

            {/* Scan Progress */}
            {scanning && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Scanning...</span>
                  <span className="text-blue-400 font-mono">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
                <p className="text-center text-gray-500 text-xs">
                  Analyzing file against 60+ antivirus engines
                </p>
              </div>
            )}

            {/* Scan Button */}
            <div className="mt-6 text-center">
              <Button
                onClick={handleScan}
                disabled={!file || scanning}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                size="lg"
                data-testid="scanner-scan-btn"
              >
                {scanning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Scan File
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <Card className={`bg-gray-900/50 border-${getStatusColor()}-500/30 animate-fade-in`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-3 font-['Outfit']">
                <StatusIcon />
                Scan Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${
                result.is_safe === true ? 'bg-green-500/10 border border-green-500/30' :
                result.is_safe === false ? 'bg-red-500/10 border border-red-500/30' :
                'bg-yellow-500/10 border border-yellow-500/30'
              }`}>
                <div className="flex items-center gap-3">
                  <StatusIcon />
                  <div>
                    <p className={`font-semibold ${
                      result.is_safe === true ? 'text-green-400' :
                      result.is_safe === false ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {result.is_safe === true ? 'No Threats Detected' :
                       result.is_safe === false ? 'Threats Detected!' :
                       'Scan Inconclusive'}
                    </p>
                    <p className="text-gray-400 text-sm">
                      Detection ratio: {result.detection_ratio || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* File Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-gray-800/30">
                  <p className="text-gray-500 text-xs uppercase mb-1">Filename</p>
                  <p className="text-white font-mono text-sm truncate">{result.filename}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/30">
                  <p className="text-gray-500 text-xs uppercase mb-1">File Size</p>
                  <p className="text-white font-mono text-sm">{formatFileSize(result.file_size)}</p>
                </div>
                <div className="col-span-2 p-4 rounded-lg bg-gray-800/30">
                  <p className="text-gray-500 text-xs uppercase mb-1">SHA-256 Hash</p>
                  <p className="text-white font-mono text-xs break-all">{result.file_hash}</p>
                </div>
              </div>

              {/* Scan Stats */}
              {result.scan_results && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-2xl font-bold text-red-400">{result.scan_results.malicious || 0}</p>
                    <p className="text-xs text-gray-500">Malicious</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-2xl font-bold text-yellow-400">{result.scan_results.suspicious || 0}</p>
                    <p className="text-xs text-gray-500">Suspicious</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-2xl font-bold text-green-400">{result.scan_results.harmless || 0}</p>
                    <p className="text-xs text-gray-500">Harmless</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-gray-500/10 border border-gray-500/20">
                    <p className="text-2xl font-bold text-gray-400">{result.scan_results.undetected || 0}</p>
                    <p className="text-xs text-gray-500">Undetected</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <Card className="bg-gray-900/30 border-white/5">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">60+</p>
              <p className="text-gray-500 text-sm">Antivirus Engines</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/30 border-white/5">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">Free</p>
              <p className="text-gray-500 text-sm">Always Free to Use</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/30 border-white/5">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400">Secure</p>
              <p className="text-gray-500 text-sm">Files Not Stored</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
